import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const PhotoUpload = ({ photos = [], onChange, maxPhotos = 10, maxSizeMB = 5 }) => {
  const [previews, setPreviews] = useState(photos);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    if (previews.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    const newPhotos = [];
    
    for (const file of files) {
      // Check file size
      if (file.size > maxSizeMB * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is ${maxSizeMB}MB`);
        continue;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      // Convert to base64
      try {
        const base64 = await fileToBase64(file);
        newPhotos.push(base64);
      } catch (error) {
        toast.error(`Failed to process ${file.name}`);
      }
    }

    const updatedPhotos = [...previews, ...newPhotos];
    setPreviews(updatedPhotos);
    onChange(updatedPhotos);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    const updatedPhotos = previews.filter((_, i) => i !== index);
    setPreviews(updatedPhotos);
    onChange(updatedPhotos);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById('photo-upload').click()}
          className="bg-gray-800 border-gray-700"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Photos
        </Button>
        <span className="text-sm text-gray-400">
          {previews.length}/{maxPhotos} photos
        </span>
      </div>
      
      <input
        id="photo-upload"
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {previews.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {previews.map((photo, idx) => (
            <div key={idx} className="relative group aspect-square">
              <img
                src={photo}
                alt={`Upload ${idx + 1}`}
                className="w-full h-full object-cover rounded-lg border border-gray-700"
              />
              <button
                type="button"
                onClick={() => removePhoto(idx)}
                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {previews.length === 0 && (
        <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center">
          <ImageIcon className="w-12 h-12 mx-auto text-gray-600 mb-2" />
          <p className="text-gray-400 text-sm">No photos uploaded</p>
          <p className="text-gray-500 text-xs mt-1">Click "Upload Photos" to add images</p>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
